// Composer les groupes d'un exercice (D-052), comme dans Playbook, en mieux : autant de
// groupes que voulu, chacun avec sa chasuble ; on choisit le groupe, puis on touche les
// joueuses (une touche sur une joueuse d'un groupe la renvoie « sans groupe ») ; reprise des groupes de l'exercice
// précédent. D-176 : « Mélanger » devient « Tirage au sort » (src/ui/tirage.ts) : niveau, postes, catégorie, mixité,
// liens ; « Verrouiller » en fait les groupes, au même format ; « Montrer le tirage sur la TV ».
import { BIBS, BIB_HEX, BIB_INK, BIB_LABEL, addGroup, assign, presentOnly, shuffle, unassigned, type Bib, type ExoGroup } from '../domain/groups';
import { toGroups } from '../domain/tirage';
import type { TeamGender } from '../domain/labels';
import { $, $$, html, mount, openSheet, type Html } from './dom';
import { bibName, mountTirage, showTirageTv, tvFromGroups, type TirageWho } from './tirage';

export interface Who { id: string; name: string }

/** Une pastille de chasuble (couleur ou « sans »). */
export const bibDot = (bib: Bib | null): Html => html`<span class="gx-dot ${bib ? '' : 'gx-dot-none'}" style="${bib ? `background:${BIB_HEX[bib]}` : ''}" aria-hidden="true"></span>`;

/** Résumé des groupes d'un exercice (frise de la séance, mode direct). */
export const groupsLine = (groups: ExoGroup[]): Html =>
  groups.length ? html`<span class="gx-line">${groups.map(g => html`${bibDot(g.bib)}`)}<span>${groups.length} ${groups.length > 1 ? 'groupes' : 'groupe'}</span></span>` : html``;

/** Les groupes, avec les prénoms (mode direct : ce que le coach lit au bord du terrain). */
export const groupsDetail = (groups: ExoGroup[], nameOf: (id: string) => string): Html => html`
  <ul class="gx-detail">${groups.map(g => html`<li>${bibDot(g.bib)}<strong>${g.name}</strong><span>${g.players.map(nameOf).join(', ') || '—'}</span></li>`)}</ul>`;

/** Le tirage au sort dans la fiche des groupes (D-176) : l'équipe, tout le monde convoqué (prénom, numéro), les présentes. */
export interface GroupsTirage {
  team: { id: string; name: string; gender: TeamGender; category: string | null };
  people: TirageWho[];
  tvSub: string;
  /** Ouvrir sur « Tirage au sort » (par défaut : quand l'exercice n'a pas encore de groupes). */
  start?: 'main' | 'tirage';
}

export function openGroupsSheet(title: string, initial: ExoGroup[], present: Who[], previous: ExoGroup[] | null, onSave: (groups: ExoGroup[], how?: 'tirage') => void, tirage?: GroupsTirage): void {
  const ids = present.map(p => p.id);
  const nameOf = new Map(present.map(p => [p.id, p.name]));
  let groups: ExoGroup[] = initial.map(g => ({ ...g, players: [...g.players] }));
  let sel = 0;
  let gmode: 'main' | 'tirage' = tirage ? (tirage.start ?? (groups.length ? 'main' : 'tirage')) : 'main';

  openSheet(`Groupes · ${title}`, html`${tirage ? html`<div class="seg tr-modes" role="group" aria-label="Composer les groupes" data-guide="tr-mode">
      <button type="button" data-gmode="main" aria-pressed="${String(gmode === 'main')}">À la main</button>
      <button type="button" data-gmode="tirage" aria-pressed="${String(gmode === 'tirage')}">Tirage au sort</button></div>` : ''}
    <div id="gx" ${gmode === 'main' ? '' : 'hidden'}></div><div id="gx-tir" ${gmode === 'tirage' ? '' : 'hidden'}></div>`, (sheet, close) => {
    const host = $(sheet, '#gx')!;
    const tirHost = $(sheet, '#gx-tir')!;
    let tirMounted = false;
    const showMode = () => {
      for (const b of $$<HTMLButtonElement>(sheet, '[data-gmode]')) b.setAttribute('aria-pressed', String(b.dataset.gmode === gmode));
      host.hidden = gmode !== 'main'; tirHost.hidden = gmode !== 'tirage';
      if (gmode === 'tirage' && tirage && !tirMounted) {
        tirMounted = true;
        mountTirage(tirHost, {
          team: tirage.team, people: tirage.people, present: ids, teams: groups.length >= 2 ? groups.length : ids.length >= 12 ? 3 : 2,
          tv: { title: 'Tirage des équipes', sub: tirage.tvSub },
          onLock: r => { close(); onSave(toGroups(r, b => bibName(b, tirage.team.gender)), 'tirage'); },
        });
      }
    };
    for (const b of $$<HTMLButtonElement>(sheet, '[data-gmode]')) b.addEventListener('click', () => { gmode = b.dataset.gmode === 'tirage' ? 'tirage' : 'main'; showMode(); });
    const draw = () => {
      const shown = presentOnly(groups, ids);
      const pool = unassigned(groups, ids);
      if (sel >= groups.length) sel = Math.max(0, groups.length - 1);
      mount(host, html`
        ${groups.length ? html`
          <div class="gx-groups">${shown.map((g, i) => html`
            <section class="gx-g ${i === sel ? 'on' : ''}" data-sel="${i}" style="--bib:${g.bib ? BIB_HEX[g.bib] : 'var(--border)'}">
              <header>
                <button type="button" class="gx-bib" data-bib="${i}" style="${g.bib ? `background:${BIB_HEX[g.bib]};color:${BIB_INK[g.bib]}` : ''}"
                  aria-label="Chasuble ${g.bib ? BIB_LABEL[g.bib] : 'aucune'} : changer">${g.bib ? '' : '∅'}</button>
                <input class="gx-name" data-name="${i}" value="${g.name}" maxlength="40" aria-label="Nom du groupe">
                <span class="gx-n">${g.players.length}</span>
                <button type="button" class="link-x" data-rm="${i}" aria-label="Retirer ${g.name}">retirer</button>
              </header>
              <div class="gx-chips">${g.players.length ? g.players.map(p => html`<button type="button" class="gx-chip" data-out="${p}">${nameOf.get(p) ?? '—'}</button>`)
                : html`<span class="muted small">${i === sel ? 'Touche les noms ci-dessous.' : 'Touche ce groupe pour le remplir.'}</span>`}</div>
            </section>`)}
          </div>
          <section class="gx-pool">
            <h4>Sans groupe · ${pool.length}${groups[sel] ? html` <span class="muted small">→ une touche les met dans « ${groups[sel]!.name} »</span>` : ''}</h4>
            <div class="gx-chips">${pool.length ? pool.map(p => html`<button type="button" class="gx-chip gx-free" data-in="${p}">${nameOf.get(p) ?? '—'}</button>`)
              : html`<span class="muted small">Toutes les présentes ont un groupe.</span>`}</div>
          </section>`
        : html`<p class="muted">Tout le monde ensemble sur cet exercice. Fais des groupes pour les ateliers, les oppositions, les rotations.</p>`}
        <div class="gx-tools">
          <button type="button" class="btn btn-ghost btn-sm" id="gx-add">+ Groupe</button>
          ${groups.length > 1 && !tirage ? html`<button type="button" class="btn btn-ghost btn-sm" id="gx-mix">Mélanger</button>` : ''}
          ${tirage && groups.some(g => g.bib && g.players.length) ? html`<button type="button" class="btn btn-ghost btn-sm" id="gx-tv">Montrer sur la TV</button>` : ''}
          ${previous?.length ? html`<button type="button" class="btn btn-ghost btn-sm" id="gx-prev">Groupes de l'exercice d'avant</button>` : ''}
          ${groups.length ? html`<button type="button" class="btn btn-link btn-sm" id="gx-clear">Tout le monde ensemble</button>` : ''}
        </div>
        <p class="hint">${present.length} ${present.length > 1 ? 'présentes' : 'présente'}. Les absentes n'apparaissent pas ; si l'une confirme sa venue, elle retrouve son groupe.</p>
        <button type="button" class="btn btn-primary" id="gx-save">Enregistrer</button>`);

      host.querySelectorAll<HTMLElement>('[data-sel]').forEach(s => s.addEventListener('click', e => {
        if ((e.target as HTMLElement).closest('button, input')) return;
        sel = Number(s.dataset.sel); draw();
      }));
      host.querySelectorAll<HTMLButtonElement>('[data-bib]').forEach(b => b.addEventListener('click', () => {
        const i = Number(b.dataset.bib), cur = groups[i]!.bib;
        const taken = new Set(groups.filter((_, k) => k !== i).map(g => g.bib));
        const cycle: (Bib | null)[] = [...BIBS, null];
        let k = cycle.indexOf(cur);
        do { k = (k + 1) % cycle.length; } while (cycle[k] !== null && taken.has(cycle[k]!) && cycle[k] !== cur);
        groups = groups.map((g, j) => (j === i ? { ...g, bib: cycle[k]! } : g));
        sel = i; draw();
      }));
      host.querySelectorAll<HTMLInputElement>('[data-name]').forEach(inp => inp.addEventListener('change', () => {
        const i = Number(inp.dataset.name);
        groups = groups.map((g, j) => (j === i ? { ...g, name: inp.value.trim().slice(0, 40) || g.name } : g));
      }));
      host.querySelectorAll<HTMLButtonElement>('[data-rm]').forEach(b => b.addEventListener('click', () => {
        groups = groups.filter((_, j) => j !== Number(b.dataset.rm)); draw();
      }));
      host.querySelectorAll<HTMLButtonElement>('[data-out]').forEach(b => b.addEventListener('click', () => { groups = assign(groups, b.dataset.out!, -1); draw(); }));
      host.querySelectorAll<HTMLButtonElement>('[data-in]').forEach(b => b.addEventListener('click', () => {
        if (!groups.length) return;
        groups = assign(groups, b.dataset.in!, sel); draw();
      }));
      $(host, '#gx-add')!.addEventListener('click', () => {
        const first = !groups.length;
        groups = first ? addGroup(addGroup([])) : addGroup(groups);
        sel = first ? 0 : groups.length - 1; draw();
      });
      $(host, '#gx-mix')?.addEventListener('click', () => { groups = shuffle(groups, ids); draw(); });
      $(host, '#gx-tv')?.addEventListener('click', () => {
        const num = new Map(tirage!.people.map(p => [p.id, p.jersey]));
        showTirageTv({ title: 'Tirage des équipes', sub: tirage!.tvSub, numbers: false, teams: tvFromGroups(presentOnly(groups, ids), id => nameOf.get(id) ?? '—', id => num.get(id) ?? null) });
      });
      $(host, '#gx-prev')?.addEventListener('click', () => { groups = previous!.map(g => ({ ...g, players: [...g.players] })); sel = 0; draw(); });
      $(host, '#gx-clear')?.addEventListener('click', () => { groups = []; draw(); });
      $(host, '#gx-save')!.addEventListener('click', () => { close(); onSave(groups); });
    };
    draw();
    showMode();
  });
}
